/**
 * BlueSage's refusals, in words a cashier or shopper can act on
 * (docs/PAYMENTS.md §3).
 *
 * BlueSage: "Map on `code`. Never string-match `detail`. The list of codes is
 * open." So every known code has its words here, and anything else falls back
 * to the words for its kind of failure — never to BlueSage's own text, which
 * is written for developers.
 */

export interface ProviderProblem {
  readonly status: number;
  readonly code: string;
  /** Extra members BlueSage sends with some codes (max_amount, refundable_amount…). */
  readonly extra?: Readonly<Record<string, unknown>>;
}

const money = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? `$${value.toFixed(2)}` : null;

export function problemWords(problem: ProviderProblem): string {
  switch (problem.code) {
    case 'terminal_offline':
      return 'That terminal is offline. Check it is on and connected, or choose another.';
    case 'terminal_not_found':
      return 'BlueSage does not know that terminal at this location. Check Settings → Card payments, or ask BlueSage to add it.';
    case 'terminal_status_unknown':
      return 'The card service could not reach the terminal just now. Try again.';
    case 'terminal_push_gated':
      return 'Sending to terminals is not switched on for this store yet. Ask BlueSage to turn it on.';
    case 'amount_exceeds_location_limit': {
      const most = money(problem.extra?.max_amount);
      return most === null
        ? 'That is more than this store can take on one card payment.'
        : `That is more than this store can take on one card payment. The most is ${most}.`;
    }
    case 'refund_window_closed':
      return 'That card payment is too old to refund to the card. Refund it another way.';
    case 'transaction_not_refundable':
      return 'That card payment cannot be refunded to the card. Refund it another way.';
    case 'refund_not_allowed':
      return 'Refunds to cards are switched off for this store at BlueSage. Refund it another way.';
    case 'refund_exceeds_remaining': {
      const left = money(problem.extra?.refundable_amount);
      return left === null
        ? 'That is more than is left to refund on that card.'
        : `That is more than is left to refund on that card. ${left} is left.`;
    }
    case 'refund_failed':
      return 'The card refund was refused. Refund it another way.';
    case 'push_not_cancellable':
      return 'That payment has already finished on the terminal.';
    case 'profile_deleted':
      return 'That saved card has been removed.';
    case 'rate_limited':
      return 'The card service is busy. Try again in a moment.';
    case 'valor_unreachable':
    case 'valor_client_token_failed':
    case 'vc_publish_failed':
    case 'valor_devices_failed':
      return 'The card processor did not answer. Try again in a moment.';
    case 'invalid_parent_origin':
    case 'invalid_frame_ancestors':
      return 'The card form could not be shown on this page. Tell the store.';
    case 'unauthorized':
    case 'invalid_api_key':
      return 'BlueSage did not accept the store’s key. Check it in Settings → Card payments.';
    default:
      break;
  }
  if (problem.status === 401 || problem.status === 403) {
    return 'BlueSage did not accept the store’s key. Check it in Settings → Card payments.';
  }
  if (problem.status === 404) {
    return 'BlueSage does not know that location or payment. Check Settings → Card payments.';
  }
  if (problem.status === 429) return 'The card service is busy. Try again in a moment.';
  if (problem.status >= 500) return 'The card service did not answer. Try again in a moment.';
  return 'The card service refused that. Try again, or take the payment another way.';
}

/** BlueSage refused a refund because the card itself cannot take it: offer another way. */
export function refundNeedsAnotherWay(code: string): boolean {
  return (
    code === 'refund_window_closed' ||
    code === 'transaction_not_refundable' ||
    code === 'refund_not_allowed' ||
    code === 'refund_failed'
  );
}

/** How the card was read, as a receipt says it. */
export function entryModeWords(mode: string | null): string | null {
  switch (mode) {
    case 'contactless':
      return 'Tapped';
    case 'chip':
      return 'Chip';
    case 'swiped':
      return 'Swiped';
    case 'keyed':
      return 'Keyed';
    default:
      return null;
  }
}
